import { useMemo, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import "./AppShell.css";
import { useAuthStore } from "@/auth";
import { useUiStore } from "./uiStore";
import { useT } from "./i18n";
import { FEATURES } from "./registry";
import type { NavItem } from "./featureModule";

interface ResolvedNavItem extends NavItem {
  moduleId: string;
}

/**
 * AppShell — navy top bar + collapsible side nav + content region (Direction A « Atelier »). The
 * nav is composed from the FEATURE REGISTRY and filtered live by capability: a user only sees the
 * items they may reach (module caps ∪ item caps must ALL be held). Density + locale toggles live in
 * the top bar. The routed feature content renders in <Outlet/>.
 */
export function AppShell() {
  const { t } = useT();
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const density = useUiStore((s) => s.density);
  const setDensity = useUiStore((s) => s.setDensity);
  const locale = useUiStore((s) => s.locale);
  const setLocale = useUiStore((s) => s.setLocale);
  const online = useUiStore((s) => s.online);

  const capabilities = useAuthStore((s) => s.capabilities);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Flatten + capability-filter the nav, grouped by section, ordered.
  const sections = useMemo(() => {
    const items: ResolvedNavItem[] = [];
    for (const mod of FEATURES) {
      const moduleAllowed = mod.requiredCapabilities.every((c) => capabilities.has(c));
      if (!moduleAllowed) continue;
      for (const item of mod.nav) {
        const itemAllowed = (item.requiredCapabilities ?? []).every((c) => capabilities.has(c));
        if (itemAllowed) items.push({ ...item, moduleId: mod.id });
      }
    }
    items.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
    const grouped = new Map<string, ResolvedNavItem[]>();
    for (const it of items) {
      const key = it.section ?? "";
      const arr = grouped.get(key) ?? [];
      arr.push(it);
      grouped.set(key, arr);
    }
    return grouped;
  }, [capabilities]);

  const renderLabel = (label: ReactNode): ReactNode =>
    typeof label === "string" && label.includes(".") ? t(label) : label;

  return (
    <div className="nv-shell">
      {/* WCAG 2.4.1 Bypass Blocks — keyboard skip-link to the main content region. */}
      <a href="#nv-main" className="nv-skip-link">
        {t("action.skipToContent")}
      </a>
      <header className="nv-topbar">
        <button
          type="button"
          className="nv-topbar__btn"
          aria-label={t("nav.toggle")}
          aria-expanded={!navCollapsed}
          aria-controls="nv-primary-nav"
          onClick={toggleNav}
        >
          <span aria-hidden>☰</span>
        </button>
        <span className="nv-topbar__brand">
          {t("app.name")}
          <span className="nv-topbar__sig">{t("app.signature")}</span>
        </span>
        <span className="nv-topbar__spacer" />
        <div className="nv-topbar__actions">
          <button
            type="button"
            className="nv-topbar__btn"
            aria-label={t("density.toggle")}
            aria-pressed={density === "compact"}
            onClick={() => setDensity(density === "confort" ? "compact" : "confort")}
          >
            {density === "confort" ? t("density.confort") : t("density.compact")}
          </button>
          <button
            type="button"
            className="nv-topbar__btn"
            onClick={() => setLocale(locale === "fr" ? "ar" : "fr")}
            aria-label={t("locale.toggle")}
          >
            <span aria-hidden>{locale === "fr" ? "FR" : "ع"}</span>
          </button>
          {user && (
            <button type="button" className="nv-topbar__btn" onClick={() => void logout()}>
              {t("action.logout")}
            </button>
          )}
        </div>
      </header>

      <nav
        id="nv-primary-nav"
        className={navCollapsed ? "nv-nav nv-nav--collapsed" : "nv-nav"}
        aria-label={t("nav.primary")}
      >
        {[...sections.entries()].map(([section, items]) => (
          <div key={section || "_"}>
            {section && <div className="nv-nav__section">{renderLabel(section)}</div>}
            {items.map((item) => (
              <NavLink
                key={`${item.moduleId}:${item.id}`}
                to={item.path}
                className={({ isActive }) => (isActive ? "nv-nav__link is-active" : "nv-nav__link")}
                title={typeof item.label === "string" ? renderLabel(item.label)?.toString() : undefined}
              >
                <span className="nv-nav__icon" aria-hidden>
                  {item.icon ?? "•"}
                </span>
                <span className="nv-nav__label">{renderLabel(item.label)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <main id="nv-main" className="nv-content" tabIndex={-1} aria-label={t("region.main")}>
        {!online && (
          <div className="nv-offline-banner" role="status">
            <span aria-hidden>⚠</span>
            {t("state.offline.title")} — {t("action.retry")}
          </div>
        )}
        <div className="nv-content__inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
